import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface SearchResult {
  id: string;
  type: 'customer' | 'order' | 'measurement';
  title: string;
  subtitle: string;
  description: string;
  date: string;
  status?: string;
  priority?: string;
  price?: number;
  photo?: string;
}

interface SearchHistory {
  id: string;
  query: string;
  timestamp: string;
  resultCount: number;
}

const SearchHistory: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<SearchHistory[]>([]);

  // TODO: Load real search history from API
  const [mockSearchHistory, setMockSearchHistory] = useState<SearchHistory[]>([]);

  // TODO: Load real search results from API
  const [mockSearchResults, setMockSearchResults] = useState<SearchResult[]>([]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ready':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'completed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'customer':
        return '👤';
      case 'order':
        return '📋';
      case 'measurement':
        return '📏';
      default:
        return '🔍';
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    
    // Simulate API call delay
    setTimeout(() => {
      // Filter mock results based on search query
      const filtered = mockSearchResults.filter(result => 
        result.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        result.subtitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        result.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      setSearchResults(filtered);
      setIsSearching(false);
      
      // Add to recent searches
      const newSearch: SearchHistory = {
        id: Date.now().toString(),
        query: searchQuery,
        timestamp: new Date().toISOString(),
        resultCount: filtered.length
      };
      setRecentSearches(prev => [newSearch, ...prev.slice(0, 9)]);
    }, 1000);
  };

  const handleQuickSearch = (query: string) => {
    setSearchQuery(query);
    // Trigger search automatically
    setTimeout(() => handleSearch(), 100);
  };

  const renderSearchResult = (result: SearchResult) => {
    return (
      <div key={result.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-2xl mr-4">
                {result.photo || getTypeIcon(result.type)}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-semibold text-gray-900">{result.title}</h3>
                  <span className="text-xs text-gray-500 uppercase">{result.type}</span>
                  {result.status && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(result.status)}`}>
                      {result.status.replace('_', ' ')}
                    </span>
                  )}
                  {result.priority && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(result.priority)}`}>
                      {result.priority.toUpperCase()}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-1">{result.subtitle}</p>
                <p className="text-sm text-gray-500 mb-2">{result.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{formatDate(result.date)}</span>
                  {result.price && (
                    <span className="text-sm font-medium text-green-600">{formatCurrency(result.price)}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex space-x-2">
              <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                View Details
              </button>
              {result.type === 'customer' && (
                <button className="text-green-600 hover:text-green-800 text-sm font-medium">
                  New Order
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/tailor-dashboard" className="text-gray-400 hover:text-gray-600 mr-4">
                <span className="text-2xl">←</span>
              </Link>
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                <span className="text-white text-lg font-bold">VT</span>
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">Search & History</span>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowImageSearch(!showImageSearch)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">📷</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by name, phone, order ID, cloth type, or any details..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching ? (
                  <span className="flex items-center">
                    <span className="animate-spin mr-2">⏳</span>
                    Searching...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <span className="mr-2">🔍</span>
                    Search
                  </span>
                )}
              </button>
            </div>

            {/* Search Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Type</label>
                <select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="customer">Customers Only</option>
                  <option value="order">Orders Only</option>
                  <option value="measurement">Measurements Only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Clear Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Image Search Section */}
        {showImageSearch && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📷</span>
                Image Search (Coming Soon)
              </h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <div className="text-4xl mb-4">📷</div>
                <p className="text-gray-600 mb-4">Upload a customer photo to find similar customers</p>
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  Upload Photo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Search Results ({searchResults.length})
              </h2>
              <span className="text-sm text-gray-500">
                Found {searchResults.length} results for "{searchQuery}"
              </span>
            </div>
            <div className="space-y-4">
              {searchResults.map(renderSearchResult)}
            </div>
          </div>
        )}

        {/* Quick Search Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Search Tips */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">💡</span>
                Search Tips
              </h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Search by customer name or phone number</li>
                <li>• Search by order ID (TAILOR-1-0001, TAILOR-1-0002)</li>
                <li>• Search by cloth type (cotton, silk, wool)</li>
                <li>• Search by order status (pending, ready, completed)</li>
                <li>• Search by measurements (chest, waist, sleeve)</li>
                <li>• Use quotes for exact phrases</li>
              </ul>
            </div>
          </div>

          {/* Quick Filters */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">⚡</span>
                Quick Filters
              </h3>
              <div className="space-y-2">
                <button 
                  onClick={() => handleQuickSearch("pending orders")}
                  className="block w-full text-left text-sm text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-50"
                >
                  📋 Pending Orders
                </button>
                <button 
                  onClick={() => handleQuickSearch("ready for pickup")}
                  className="block w-full text-left text-sm text-green-600 hover:text-green-800 p-2 rounded hover:bg-green-50"
                >
                  📦 Ready for Pickup
                </button>
                <button 
                  onClick={() => handleQuickSearch("overdue")}
                  className="block w-full text-left text-sm text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50"
                >
                  ⚠️ Overdue Orders
                </button>
                <button 
                  onClick={() => handleQuickSearch("repeat customers")}
                  className="block w-full text-left text-sm text-purple-600 hover:text-purple-800 p-2 rounded hover:bg-purple-50"
                >
                  👥 Repeat Customers
                </button>
                <button 
                  onClick={() => handleQuickSearch("high priority")}
                  className="block w-full text-left text-sm text-orange-600 hover:text-orange-800 p-2 rounded hover:bg-orange-50"
                >
                  🔥 High Priority Orders
                </button>
              </div>
            </div>
          </div>

          {/* Recent Searches */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🕒</span>
                Recent Searches
              </h3>
              <div className="space-y-2">
                {mockSearchHistory.map((search) => (
                  <button
                    key={search.id}
                    onClick={() => handleQuickSearch(search.query)}
                    className="block w-full text-left text-sm text-gray-600 hover:text-gray-800 p-2 rounded hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{search.query}</span>
                      <span className="text-xs text-gray-400">
                        {formatTime(search.timestamp)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">
                      {search.resultCount} results
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {searchQuery && searchResults.length === 0 && !isSearching && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your search terms or use different keywords
            </p>
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Search suggestions:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {['Customer search', 'Suit orders', 'Pending deliveries', 'Cotton shirts'].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => handleQuickSearch(suggestion)}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchHistory; 